import { Outlet, Route, Routes } from 'react-router'

import { Card, EmptyState } from '@/components/Card'
import { ScreenHeader } from '@/components/ScreenHeader'
import { TabBar } from '@/components/TabBar'
import { CaptureSheet } from '@/routes/CaptureSheet'
import { CardForm } from '@/routes/CardForm'
import { Avisos } from '@/routes/Avisos'
import { AvisosAjustes } from '@/routes/AvisosAjustes'
import { Cards } from '@/routes/Cards'
import { Dashboard } from '@/routes/Dashboard'
import { GoalContribution, GoalForm } from '@/routes/GoalForm'
import { ConfirmarIngreso } from '@/routes/ConfirmarIngreso'
import { Informes } from '@/routes/Informes'
import { Justificar } from '@/routes/Justificar'
import { Meses } from '@/routes/Meses'
import { MesesForm } from '@/routes/MesesForm'
import { Reparto } from '@/routes/Reparto'
import { Revisiones } from '@/routes/Revisiones'
import { Lab } from '@/routes/Lab'
import { Recurring } from '@/routes/Recurring'
import { RegistrarIngreso } from '@/routes/RegistrarIngreso'
import { RecurringForm } from '@/routes/RecurringForm'
import { Savings } from '@/routes/Savings'
import { Settings } from '@/routes/Settings'
import { Transactions } from '@/routes/Transactions'

export function App() {
  return (
    <Routes>
      {/* La captura vive fuera del Shell: ocupa la pantalla completa y no
          compite con la navegación mientras se escribe el monto. */}
      <Route path="capturar" element={<CaptureSheet />} />
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route path="movimientos" element={<Transactions />} />
        <Route path="tarjetas" element={<Cards />} />
        {/* El formulario de tarjeta es una hoja a pantalla completa, pero vive
            dentro del Shell: al cerrarla se vuelve a la lista con la barra de
            navegación ya dibujada, sin el parpadeo de remontar la pantalla. */}
        {/* Literales antes que el parámetro: si `tarjetas/:id` fuera primero,
            capturaría «meses» como si fuera el id de una tarjeta. */}
        <Route path="tarjetas/meses" element={<Meses />} />
        <Route path="tarjetas/meses/nueva" element={<MesesForm />} />
        <Route path="tarjetas/meses/:id/editar" element={<MesesForm />} />
        <Route path="tarjetas/nueva" element={<CardForm />} />
        <Route path="tarjetas/:id/editar" element={<CardForm />} />
        <Route path="ahorro" element={<Savings />} />
        {/* Las rutas literales van antes que las de parámetro: si `ahorro/:id`
            se declarara primero, capturaría «reparto» y «revisiones» como si
            fueran identificadores de meta. */}
        <Route path="ahorro/reparto" element={<Reparto />} />
        <Route path="ahorro/revisiones" element={<Revisiones />} />
        <Route path="ahorro/nueva" element={<GoalForm />} />
        <Route path="ahorro/:id/editar" element={<GoalForm />} />
        <Route path="ahorro/:id/aportar" element={<GoalContribution />} />
        <Route path="fijos" element={<Recurring />} />
        <Route path="fijos/nuevo" element={<RecurringForm />} />
        <Route path="fijos/:id/editar" element={<RecurringForm />} />
        {/* La literal antes que la de parámetro: `ingresos/:id` capturaría
            «ingreso» si fuera al revés. */}
        <Route path="ingreso" element={<RegistrarIngreso />} />
        <Route path="ingresos/:id" element={<ConfirmarIngreso />} />
        <Route path="justificar" element={<Justificar />} />
        <Route path="avisos" element={<Avisos />} />
        <Route path="avisos/ajustes" element={<AvisosAjustes />} />
        <Route path="informes" element={<Informes />} />
        <Route path="ajustes" element={<Settings />} />
        {/* Banco de pruebas de componentes. Solo en desarrollo:
            `import.meta.env.DEV` se resuelve en tiempo de compilación, así que
            ni la ruta ni el componente entran en el paquete de producción. */}
        {import.meta.env.DEV ? <Route path="lab" element={<Lab />} /> : null}
        <Route path="*" element={<Placeholder name="No encontramos esta página" />} />
      </Route>
    </Routes>
  )
}

/**
 * El Shell no dibuja encabezado.
 *
 * El inicio necesita que su zócalo marino llegue de borde a borde y hasta
 * debajo de la barra de estado; un encabezado común lo impediría. Cada pantalla
 * dibuja el suyo y el Shell solo aporta el lienzo, el relleno inferior y la
 * navegación.
 */
function Shell() {
  return (
    <div className="min-h-dvh bg-bg">
      {/* El relleno inferior deja libre la cápsula de navegación más el área
          segura del indicador de gestos; sin él, la última fila queda
          intocable. */}
      <main className="pb-[calc(6.5rem+env(safe-area-inset-bottom))]">
        <Outlet />
      </main>

      <TabBar />
    </div>
  )
}

function Placeholder({ name }: { name: string }) {
  return (
    <>
      <ScreenHeader title={name} />
      <div className="px-safe mx-auto max-w-lg">
        <Card className="mt-2">
          <EmptyState
            title={name}
            description="Esta sección llega en la siguiente fase. El motor financiero que la alimenta ya está listo y probado."
          />
        </Card>
      </div>
    </>
  )
}

export default App
